import { useState } from 'react'

import {
  LoginSchemaFields,
  LoginSchemaMethods,
} from '../components/form/schema'
import { useLogin } from './use-login'
import { useSsoLogin } from './use-sso-login'

export type LoginStep = 1 | 2

const FIRST_STEP: LoginStep = 1
const LAST_STEP: LoginStep = 2

const stepFields: Record<LoginStep, LoginSchemaFields[]> = {
  1: ['email'],
  2: ['email', 'password'],
}

const getEmailDomain = (email: string): string => {
  const match = email.match(/@([a-zA-Z0-9.-]+)/)
  return match ? match[1] : ''
}

export const useFormSteps = (formMethods: LoginSchemaMethods) => {
  const { state: loginState, handleLoginAction } = useLogin()
  const { state: ssoState, handleSSOAction } = useSsoLogin()

  const [currentStep, setCurrentStep] = useState<LoginStep>(FIRST_STEP)

  const isFirstStep = currentStep === FIRST_STEP
  const isLastStep = currentStep === LAST_STEP

  const isLoading = loginState.type === 'loading' || ssoState.type === 'loading'

  const goToPreviousStep = () => {
    setCurrentStep((step) =>
      step > FIRST_STEP ? ((step - 1) as LoginStep) : step,
    )
  }

  const goToNextStep = async () => {
    const { trigger, getValues } = formMethods

    if (!(await trigger(stepFields[currentStep]))) return

    if (isFirstStep) {
      const domain = getEmailDomain(getValues('email'))

      const hasSSO = await handleSSOAction(domain)

      if (hasSSO) return

      setCurrentStep(LAST_STEP)

      return
    }

    await handleLoginAction(getValues())
  }

  return {
    isLoading,
    currentStep,
    isFirstStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
  }
}
